import type {
  ColorType,
  PromotionContent,
  PromotionContentToneType,
} from '@wconcept/wconcept-common/models/renewal/promotion';

const DEFAULT_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_TEXT_COLOR = '#000000';
const DEFAULT_POINT_COLOR = '#000000';
const DEFAULT_EMPHASIS_TEXT_COLOR = '#ffffff';

/** §0 기획 마스크 — `full`(네 값) vs `twoField`(배경·타이틀만) */
export type PromotionModuleVisualMask = 'full' | 'twoField';

export type PromotionModuleVisualTokens = {
  colorType: ColorType | undefined;
  backgroundColorHex?: string;
  titleTone?: PromotionContentToneType;
  pointColorHex?: string;
  textEmphasisTone?: PromotionContentToneType;
};

type PromotionContentVisualPick = Pick<
  PromotionContent,
  | 'colorType'
  | 'backgroundColorHex'
  | 'titleTone'
  | 'pointColorHex'
  | 'textEmphasisTone'
>;

/**
 * CUSTOM 일 때만 HEX/톤 토큰을 내려주고, 프리셋(BLACK/WHITE)은 colorType 만 둔다.
 * twoField 마스크에서는 서버가 넣더라도 point/강조는 토큰에 넣지 않는다(§0).
 */
export function toPromotionModuleVisualTokens(
  content: PromotionContentVisualPick,
  options: { mask: PromotionModuleVisualMask }
): PromotionModuleVisualTokens {
  const { colorType } = content;
  const base: PromotionModuleVisualTokens = { colorType };

  if (colorType !== 'CUSTOM') {
    return base;
  }

  const { mask } = options;
  const tokens: PromotionModuleVisualTokens = {
    ...base,
    backgroundColorHex: content.backgroundColorHex,
    titleTone: content.titleTone,
  };

  if (mask === 'full') {
    tokens.pointColorHex = content.pointColorHex;
    tokens.textEmphasisTone = content.textEmphasisTone;
  }

  return tokens;
}

export type PromotionCustomColorsFullFallbackOptions = {
  /** pointColorHex 미수신 시 (예: BASIC_NAVIGATION 의 module.mainColor) */
  pointColorFallback?: string;
};

function promotionToneToHex(
  tone: PromotionContentToneType | undefined
): string | undefined {
  if (tone === 'BLACK') return '#000000';
  if (tone === 'WHITE') return '#ffffff';
  return undefined;
}

/**
 * §0 full 마스크 — 배경·타이틀·포인트·강조 HEX.
 * renewal Navigation 등 객체형 colorType 과 필드명 정합.
 */
export type PromotionCustomColorsFull = {
  bgColor: string;
  textColor: string;
  pointColor: string;
  emphasisTextColor: string;
};

export function buildPromotionCustomColorsFullFromTokens(
  tokens: PromotionModuleVisualTokens,
  options?: PromotionCustomColorsFullFallbackOptions
): PromotionCustomColorsFull {
  return {
    bgColor: tokens.backgroundColorHex?.trim() || DEFAULT_BACKGROUND_COLOR,
    textColor: promotionToneToHex(tokens.titleTone) ?? DEFAULT_TEXT_COLOR,
    pointColor:
      tokens.pointColorHex?.trim() ||
      options?.pointColorFallback?.trim() ||
      DEFAULT_POINT_COLOR,
    emphasisTextColor:
      promotionToneToHex(tokens.textEmphasisTone) ??
      DEFAULT_EMPHASIS_TEXT_COLOR,
  };
}

/** §0 twoField 마스크 — 배경·타이틀(톤) HEX 만 */
export type PromotionCustomColorsLite = {
  bgColor: string;
  textColor: string;
};

export function buildPromotionCustomColorsLiteFromTokens(
  tokens: PromotionModuleVisualTokens
): PromotionCustomColorsLite | null {
  if (tokens.colorType !== 'CUSTOM') return null;
  return {
    bgColor: tokens.backgroundColorHex?.trim() || DEFAULT_BACKGROUND_COLOR,
    textColor: promotionToneToHex(tokens.titleTone) ?? DEFAULT_TEXT_COLOR,
  };
}

/**
 * 프리셋 black 또는 CUSTOM(full) 타이틀 흰색일 때 카드·탭·스켈레톤 등 어두운 배경 레이아웃으로 간주한다.
 */
export function isPromotionDarkSurfaceFromPresetBgAndFullColors(
  presetBgType: string,
  customFullColors: PromotionCustomColorsFull | null
): boolean {
  return (
    presetBgType === 'black' ||
    customFullColors?.textColor.trim().toLowerCase() ===
      DEFAULT_BACKGROUND_COLOR
  );
}
